import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import "./filter-pill.css";
import { ChevronDown } from "./icons.js";

/**
 * A pill that opens a panel: the shell both filters share.
 *
 * `filter-dropdown` in DESIGN.md — canvas background, `hairline-strong` border,
 * `rounded.full`, `body-sm-medium`. The chevron is a real icon rather than a
 * text glyph so it inherits `currentColor` and cannot come out as a
 * font-dependent arrow (§15a bans emoji as iconography, and "▾" is barely
 * better).
 *
 * The behaviours below are the ones a hand-rolled dropdown usually skips, and
 * each of them is the difference between a control and a trap:
 *
 *   - **Escape closes it and returns focus to the pill.** Without the second
 *     half, a keyboard user who escapes lands at the top of the document.
 *   - **A click outside closes it**, listening on `pointerdown` rather than
 *     `click` so a drag that starts inside and ends outside does not count.
 *   - **Focus leaving the panel closes it**, which is what makes Tab work at
 *     all — otherwise the panel stays open behind whatever you tabbed to.
 *   - `aria-expanded` and `aria-controls`, so the relationship is announced
 *     rather than only drawn.
 *
 * Not a `<dialog>` and not Radix: a filter is not modal — the page behind it
 * must stay readable and scrollable while you choose a range.
 */

export function FilterPill({
  label,
  value,
  detail,
  icon,
  compact,
  children,
}: {
  /** For screen readers: what this filter is. The visible text is `value`. */
  readonly label: string;
  readonly value: string;
  /** Quieter words after the value: the days a named range covers. */
  readonly detail?: string;
  readonly icon?: ReactNode;
  /**
   * Icon only, with the value read but not shown.
   *
   * For a control whose state is visible elsewhere — the column picker, whose
   * effect is the table itself. The label still reaches a screen reader, so
   * the button is never a bare glyph to anyone who cannot see the table.
   */
  readonly compact?: boolean;
  /** The panel. Receives a `close` callback so a choice can dismiss it. */
  readonly children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && wrap.current?.contains(target) === true) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // The half that is usually missed: focus has to come back somewhere a
      // keyboard user expects, which is the control they opened.
      trigger.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      className="pill-wrap"
      ref={wrap}
      onBlur={(event) => {
        // `relatedTarget` is where focus went. Null means it left the document
        // entirely — switching tabs — which is not a reason to close.
        const next = event.relatedTarget;
        if (next === null) return;
        if (next instanceof Node && wrap.current?.contains(next) === true) return;
        setOpen(false);
      }}
    >
      <button
        type="button"
        className={[
          "pill",
          open ? "pill-open" : "",
          compact === true ? "pill-compact" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        ref={trigger}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {icon}
        <span className="visually-hidden">
          {label}: {value}
          {detail === undefined ? "" : `, ${detail}`}
        </span>
        {compact === true ? null : (
          <span className="pill-value" aria-hidden="true">
            {value}
            {detail === undefined ? null : <span className="pill-detail"> {detail}</span>}
          </span>
        )}
        <ChevronDown className="pill-chevron" aria-hidden="true" />
      </button>

      {open ? (
        <div className="pill-panel" id={panelId} role="group" aria-label={label}>
          {children(() => {
            setOpen(false);
            trigger.current?.focus();
          })}
        </div>
      ) : null}
    </div>
  );
}
